import createMuiTheme, {
  Theme,
  ThemeOptions,
} from '@material-ui/core/styles/createMuiTheme';
import {
  Palette,
  PaletteOptions,
} from '@material-ui/core/styles/createPalette';

import { MaxWidths, createMaxWidths } from './createMaxWidths';
import * as CustomColors from './colors';
import * as Units from './units';

interface CustomSpacing {
  formRow: number;
  navHeight: number;
  navHeightLg: number;
}
interface CustomSpacingOptions {
  formRow?: number;
  navHeight?: number;
  navHeightLg?: number;
}

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    warning: PaletteColor;
    ok: PaletteColor;
    orange: PaletteColor;
    white: PaletteColor;
    positive: PaletteColor;
  }

  // `createMuiTheme`'s parameter, used to customize/extend theme
  interface PaletteOptions {
    ok: PaletteColor;
    orange: PaletteColor;
    white: PaletteColor;
    positive: PaletteColor;
  }
}

declare module '@material-ui/core/styles/createMuiTheme' {
  interface Theme {
    maxWidths: MaxWidths;
    palette: Palette;
    customSpacing: CustomSpacing;
  }

  // `createMuiTheme`'s parameter, used to customize/extend theme
  interface ThemeOptions {
    maxWidths?: MaxWidths;
    palette?: PaletteOptions;
    customSpacing?: CustomSpacingOptions;
  }
}

interface Options extends ThemeOptions {
  // we want to make pallete a not optional property
  palette?: PaletteOptions;
}

export function createMyTheme(options: Options): Theme {
  return createMuiTheme({
    maxWidths: createMaxWidths(),
    ...options,
    palette: {
      primary: CustomColors.primary,
      secondary: CustomColors.secondary,
      error: CustomColors.negative,
      grey: CustomColors.grey,
      ok: CustomColors.positive,
      warning: CustomColors.warning,
      orange: CustomColors.orange,
      white: CustomColors.white,
      positive: CustomColors.positive,
      ...options.palette,
    },
    typography: {
      fontFamily: `'Source Sans Pro', 'Helvetica', 'Arial', 'sans-serif'`, // TODO take it from fonts
      fontSize: 14,
      ...options.typography,
    },
    customSpacing: {
      formRow: 1.4,
      navHeight: 56,
      navHeightLg: 64,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            fontSize: '1rem',
            lineHeight: '1.5',
            color: CustomColors.grey[700],
            backgroundColor: CustomColors.white.main,
          },
          img: {
            verticalAlign: 'middle',
          },
          a: {
            color: CustomColors.grey[700],
          },
        },
      },
    },
  });
}

export function createMyThemeNext(options: Options): Theme {
  return createMuiTheme({
    maxWidths: createMaxWidths(),
    ...options,
    palette: {
      primary: CustomColors.primaryNext,
      secondary: CustomColors.secondary,
      error: CustomColors.negative,
      grey: CustomColors.grey,
      ok: CustomColors.positive,
      warning: CustomColors.warning,
      orange: CustomColors.orange,
      white: CustomColors.white,
      positive: CustomColors.positive,
      ...options.palette,
    },
    typography: {
      fontFamily: `'Source Sans Pro', 'Helvetica', 'Arial', 'sans-serif'`,
      fontSize: 14,
      ...options.typography,
    },
    customSpacing: {
      formRow: 1.4,
      navHeight: 56,
      navHeightLg: 64,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            fontSize: '1rem',
            lineHeight: '1.5',
            color: CustomColors.grey[700],
            backgroundColor: CustomColors.white.main,
          },
          img: {
            verticalAlign: 'middle',
          },
          a: {
            color: CustomColors.grey[700],
          },
        },
      },
      /**
       * Theme Next Button Styles
       */
      MuiButton: {
        root: {
          fontSize: Units.rem(1),
          textTransform: 'none',
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor:
              CustomColors.ButtonsPrimaryContainedNext.backgroundHover,
          },
          '&:disabled': {
            color: CustomColors.grey[100],
            backgroundColor:
              CustomColors.ButtonsPrimaryContainedNext.backgroundDisabled,
          },
        },
        outlinedPrimary: {
          color: CustomColors.primaryNext.main,
          backgroundColor: CustomColors.ButtonsPrimaryOutlinedNext.background,
          borderColor: CustomColors.ButtonsPrimaryOutlinedNext.border,
          '&:hover': {
            borderColor: CustomColors.ButtonsPrimaryOutlinedNext.border,
            backgroundColor:
              CustomColors.ButtonsPrimaryOutlinedNext.backgroundHover,
          },
          '&:disabled': {
            color: CustomColors.grey[400],
            backgroundColor:
              CustomColors.ButtonsPrimaryOutlinedNext.backgroundDisabled,
          },
        },
      },
    },
  });
}

const theme = createMyTheme({});
export const themeNext = createMyThemeNext({});

themeNext.props = {
  MuiButton: {
    // this prop disables the drop shadow on all Buttons
    disableElevation: true,
  },
};

export { theme };
